import React, { useEffect, useState } from 'react';
import {QuickCreateModal} from "./QuickForms";
import {API} from "../../api/config";

export const PurchaseOrderForm = ({ initialData, onSubmit }) => {
    const [vendors, setVendors] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [poData, setPoData] = useState({
        vendorId: '',
        requestedBy: '',
        dateRequired: '',
        paymentMethod: 'BILLED',
        lineItems: [{ description: '', amount: '', accountId: '', attachment: null }]
    });
    const [showQuickCreate, setShowQuickCreate] = useState(false);

    useEffect(() => {
        fetchVendors();
        fetchAccounts();
        if (initialData) {
            setPoData({
                vendorId: initialData.vendor.id,
                requestedBy: initialData.requestedBy,
                dateRequired: initialData.dateRequired.split('T')[0],
                paymentMethod: initialData.paymentMethod,
                lineItems: initialData.lineItems.map(item => ({
                    description: item.description,
                    amount: item.amount,
                    accountId: item.accountId || '',
                    attachment: null
                }))
            });
        }
    }, [initialData]);

    const fetchVendors = async () => {
        try {
            const response = await fetch(API.vendors.list());
            const data = await response.json();
            setVendors(data);
        } catch (error) {
            console.error('Error fetching vendors:', error);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await fetch(API.accounts.list())
            const data = await response.json();
            setAccounts(data);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const addLineItem = () => {
        setPoData({
            ...poData,
            lineItems: [...poData.lineItems, { description: '', amount: '', accountId: '', attachment: null }]
        });
    };

    const updateLineItem = (index, field, value) => {
        const newLineItems = [...poData.lineItems];
        newLineItems[index] = { ...newLineItems[index], [field]: value };
        setPoData({ ...poData, lineItems: newLineItems });
    };

    const removeLineItem = (index) => {
        const newLineItems = poData.lineItems.filter((_, i) => i !== index);
        setPoData({ ...poData, lineItems: newLineItems });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const method = initialData ? 'PUT' : 'POST';
            const url = initialData
                ? API.purchaseOrders.update(initialData.id)
                : API.purchaseOrders.create()

            const poResponse = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    vendorId: poData.vendorId,
                    requestedBy: poData.requestedBy,
                    dateRequired: poData.dateRequired,
                    paymentMethod: poData.paymentMethod,
                    lineItems: poData.lineItems.map(({ description, amount, accountId }) => ({
                        description,
                        amount: parseFloat(amount),
                        accountId
                    }))
                }),
            });

            if (poResponse.ok) {
                await poResponse.json();
                // Handle file uploads...
                onSubmit();
            }
        } catch (error) {
            console.error('Error saving purchase order:', error);
            alert('Error saving purchase order');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 w-[80vw] max-w-6xl">
            <h2 className="text-2xl font-bold mb-6">
                {initialData ? 'Edit Purchase Order' : 'Create Purchase Order'}
            </h2>

            <div className="grid grid-cols-2 gap-6">
                <div className="flex items-end gap-2">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Vendor:</label>
                        <select
                            value={poData.vendorId}
                            onChange={(e) => setPoData({...poData, vendorId: e.target.value})}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                            required
                        >
                            <option value="">Select a vendor</option>
                            {vendors.map(vendor => (
                                <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowQuickCreate(true)}
                        className="h-10 px-3 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                    >
                        + New
                    </button>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Requested By:</label>
                    <input
                        type="text"
                        value={poData.requestedBy}
                        onChange={(e) => setPoData({...poData, requestedBy: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Date Required:</label>
                    <input
                        type="date"
                        value={poData.dateRequired}
                        onChange={(e) => setPoData({ ...poData, dateRequired: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Payment Method:</label>
                    <select
                        value={poData.paymentMethod}
                        onChange={(e) => setPoData({ ...poData, paymentMethod: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                        required
                    >
                        <option value="CASH">Cash</option>
                        <option value="CHECK">Check</option>
                        <option value="BILLED">Billed</option>
                    </select>
                </div>
            </div>

            <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Line Items</h3>
                    <button
                        type="button"
                        onClick={addLineItem}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Add Line Item
                    </button>
                </div>

                <div className="space-y-4">
                    {poData.lineItems.map((item, index) => (
                        <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                            <select
                                value={item.accountId}
                                onChange={(e) => updateLineItem(index, 'accountId', e.target.value)}
                                className="w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                required
                            >
                                <option value="">Select Account</option>
                                {accounts.map(account => (
                                    <option key={account.id} value={account.id}>
                                        {account.accountNumber} - {account.name}
                                    </option>
                                ))}
                            </select>

                            <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                placeholder="Description"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                required
                            />

                            <input
                                type="number"
                                value={item.amount}
                                onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                                placeholder="Amount"
                                className="w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                required
                            />

                            <input
                                type="file"
                                onChange={(e) => updateLineItem(index, 'attachment', e.target.files[0])}
                                className="w-48"
                            />

                            <button
                                type="button"
                                onClick={() => removeLineItem(index)}
                                className="p-2 text-red-600 hover:text-red-800"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end mt-6">
                <button
                    type="submit"
                    className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                    {initialData ? 'Update' : 'Create'} Purchase Order
                </button>
            </div>

            <QuickCreateModal
                isOpen={showQuickCreate}
                onClose={() => setShowQuickCreate(false)}
                onCreated={() => {
                    fetchVendors();
                    fetchAccounts();
                    setShowQuickCreate(false);
                }}
            />
        </form>
    );
};